import { useEffect, useRef } from 'react';
import api, { guestApiPath } from '../../../api';
import { getGuestAuthToken } from '../../../utils/guestToken';

// How long a tile whose thumbnail failed to load waits before each retry. A thumbnail that is
// still being generated normally appears within the first minutes, so the waits double from 5s to
// 80s and the tile gives up after the last one: five attempts per tile, and the placeholder stays.
export const THUMBNAIL_RETRY_DELAYS_MS = [5000, 10000, 20000, 40000, 80000];

// /view-urls accepts at most this many fileNames per call (same figure as useMediaViewUrls).
export const MAX_FILES_PER_VIEW_URLS_REQUEST = 100;

// Tiles that come due within this long of each other are retried in the same request.
const COALESCE_WINDOW_MS = 1000;
// Rounds that send a request keep at least this long between them. Tiles that fail one after
// another (a viewer scrolling through thumbnails that will not load) would otherwise each start a
// round of their own, up to one a second; with the gap a grid sends at most 12 requests a minute.
// A round that is late is harmless: it takes everything that is due, and nothing goes out early.
const MIN_ROUND_GAP_MS = 5000;
// While offline nothing is sent, but navigator.onLine is checked this often in case the `online`
// event never arrives.
const OFFLINE_RECHECK_MS = 30 * 1000;
// Each round waits its delay times a factor within +/-20%, so guests who opened the gallery
// together do not retry in lockstep. The factor is drawn once per round, not per tile, or tiles
// that failed together would drift apart and stop sharing a request.
const JITTER_RATIO = 0.2;
// Used when a 429 carries no usable Retry-After (the per-IP limiter sends 60).
const DEFAULT_RATE_LIMIT_WAIT_MS = 60 * 1000;
const MIN_RATE_LIMIT_WAIT_MS = 1000;
const MAX_RATE_LIMIT_WAIT_MS = 5 * 60 * 1000;

const PLACEHOLDER_LOGO = '/placeholder-logo.svg';

const drawJitter = () => 1 + (Math.random() * 2 - 1) * JITTER_RATIO;

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

function readRetryAfterMs(error) {
  const headers = error?.response?.headers;
  const raw = typeof headers?.get === 'function' ? headers.get('retry-after') : headers?.['retry-after'];
  let waitMs = Number.NaN;
  if (raw != null && /^\s*\d+\s*$/.test(String(raw))) {
    waitMs = Number(raw) * 1000;
  } else if (raw != null) {
    waitMs = Date.parse(String(raw)) - Date.now();
  }
  if (!Number.isFinite(waitMs)) return DEFAULT_RATE_LIMIT_WAIT_MS;
  return Math.min(MAX_RATE_LIMIT_WAIT_MS, Math.max(MIN_RATE_LIMIT_WAIT_MS, waitMs));
}

function findTileImage(filename) {
  const escaped = String(filename).replace(/["\\]/g, '\\$&');
  return document.querySelector(`[data-photo-filename="${escaped}"] img`);
}

function thumbnailUrlFrom(entry) {
  return entry?.thumbnailUrl || entry?.url || null;
}

/**
 * Retries the thumbnails of grid tiles that fell back to the placeholder logo, for the whole
 * grid at once. It exists to keep a flaky network, an expired batch of signed URLs or a signing
 * outage from turning every failed tile into its own stream of /view-urls requests: all guests on
 * a venue share one IP and its 120 requests a minute, which uploads need as well.
 *
 * - Tiles that come due together share one request, up to 100 fileNames each, sent one request
 *   at a time. Response entries are matched to tiles by fileName.
 * - A tile is retried after THUMBNAIL_RETRY_DELAYS_MS (with jitter) and given up on after the
 *   last attempt; its placeholder stays.
 * - Rounds that send a request are at least 5s apart, however tiles trickle in.
 * - A 429 spends no attempts: everything waits for Retry-After (60s when absent).
 * - Offline spends no attempts either and sends nothing; it resumes on the `online` event, or
 *   within 30s of navigator.onLine turning true if that event is missed.
 * - A tile that recovers is swapped in and forgotten; one that left the page is forgotten.
 *
 * @param {object} options
 * @param {() => string} options.getGalleryId - Current gallery tenant id
 * @param {(filename: string) => void} options.onForget - Tile no longer needs retrying (recovered
 *   or gone): drop it from the fallback set
 */
export function createThumbnailRetryScheduler({ getGalleryId, onForget }) {
  const retries = new Map(); // filename -> { attempts, dueAt }
  let timer = null;
  let inFlight = false;
  let disposed = false;
  let pausedUntil = 0;
  let lastRoundEnd = Number.NEGATIVE_INFINITY;
  let jitter = drawJitter();

  const hasAttemptsLeft = (retry) => retry.attempts < THUMBNAIL_RETRY_DELAYS_MS.length;

  function plan() {
    if (timer) clearTimeout(timer);
    timer = null;
    if (disposed || inFlight) return;
    let earliest = Number.POSITIVE_INFINITY;
    retries.forEach((retry) => {
      if (hasAttemptsLeft(retry)) earliest = Math.min(earliest, retry.dueAt);
    });
    if (earliest === Number.POSITIVE_INFINITY) return;
    if (isOffline()) {
      timer = setTimeout(plan, OFFLINE_RECHECK_MS);
      return;
    }
    const startAt = Math.max(earliest, pausedUntil, lastRoundEnd + MIN_ROUND_GAP_MS);
    timer = setTimeout(runRound, Math.max(0, startAt - Date.now()));
  }

  function forget(filename) {
    retries.delete(filename);
    onForget(filename);
  }

  function settle(chunk) {
    const dueAt = Date.now();
    chunk.forEach((filename) => {
      const retry = retries.get(filename);
      if (!retry) return;
      retry.attempts += 1;
      if (hasAttemptsLeft(retry)) retry.dueAt = dueAt + THUMBNAIL_RETRY_DELAYS_MS[retry.attempts] * jitter;
    });
  }

  function swapIn(filename, url) {
    const preload = new Image();
    preload.onload = () => {
      if (disposed) return;
      const img = findTileImage(filename);
      if (img) {
        img.src = url;
        img.className = 'w-full h-full object-cover';
      }
      forget(filename);
    };
    preload.onerror = () => {
      // Still not available: the attempt is spent and the next one is already scheduled.
      console.debug(`Thumbnail still not available for retry: ${filename}`);
    };
    preload.src = url;
  }

  // Returns 'ok', 'rate_limited' or 'offline'. Attempts are spent only for 'ok' and for a
  // failure that says nothing about the network being down.
  async function requestChunk(chunk) {
    const token = getGuestAuthToken();
    if (!token) {
      settle(chunk);
      return 'ok';
    }
    let entries;
    try {
      const response = await api.post(`${guestApiPath('/view-urls', getGalleryId())}`, {
        fileNames: chunk,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      entries = response?.data?.urls;
    } catch (error) {
      if (disposed) return 'ok';
      if (error?.response?.status === 429) {
        pausedUntil = Date.now() + readRetryAfterMs(error);
        return 'rate_limited';
      }
      if (!error?.response && isOffline()) return 'offline';
      console.debug('Thumbnail refresh failed:', error?.message || error);
      settle(chunk);
      return 'ok';
    }
    if (disposed) return 'ok';
    const byFileName = new Map();
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      if (entry?.fileName) byFileName.set(entry.fileName, entry);
    });
    settle(chunk);
    chunk.forEach((filename) => {
      const url = thumbnailUrlFrom(byFileName.get(filename));
      if (url) swapIn(filename, url);
    });
    return 'ok';
  }

  async function runRound() {
    timer = null;
    if (disposed || inFlight) return;
    const now = Date.now();
    if (isOffline() || now < pausedUntil || now < lastRoundEnd + MIN_ROUND_GAP_MS) {
      plan();
      return;
    }
    const due = [];
    retries.forEach((retry, filename) => {
      if (hasAttemptsLeft(retry) && retry.dueAt <= now + COALESCE_WINDOW_MS) due.push(filename);
    });
    // A tile that is gone, or already shows a real thumbnail, has nothing left to retry.
    const batch = due.filter((filename) => {
      const img = findTileImage(filename);
      if (img && img.src.includes(PLACEHOLDER_LOGO)) return true;
      forget(filename);
      return false;
    });
    inFlight = true;
    try {
      for (let start = 0; start < batch.length && !disposed; start += MAX_FILES_PER_VIEW_URLS_REQUEST) {
        const outcome = await requestChunk(batch.slice(start, start + MAX_FILES_PER_VIEW_URLS_REQUEST));
        if (outcome !== 'ok' || isOffline()) break;
      }
    } finally {
      inFlight = false;
    }
    jitter = drawJitter();
    if (batch.length > 0) lastRoundEnd = Date.now();
    plan();
  }

  const handleOnline = () => plan();
  if (typeof window !== 'undefined') window.addEventListener('online', handleOnline);

  return {
    /** Brings the retries in line with the tiles currently showing the placeholder. */
    sync(fallbackFilenames) {
      if (disposed) return;
      const now = Date.now();
      fallbackFilenames.forEach((filename) => {
        if (!filename || retries.has(filename)) return;
        retries.set(filename, { attempts: 0, dueAt: now + THUMBNAIL_RETRY_DELAYS_MS[0] * jitter });
      });
      retries.forEach((_retry, filename) => {
        if (!fallbackFilenames.has(filename)) retries.delete(filename);
      });
      plan();
    },
    dispose() {
      disposed = true;
      if (timer) clearTimeout(timer);
      timer = null;
      retries.clear();
      if (typeof window !== 'undefined') window.removeEventListener('online', handleOnline);
    },
  };
}

/**
 * @param {Set<string>} fallbackImages - Filenames of the tiles currently showing the placeholder
 * @param {string} galleryId
 * @param {(filename: string) => void} onForget
 */
export function useThumbnailRetries(fallbackImages, galleryId, onForget) {
  const schedulerRef = useRef(null);
  const latest = useRef({ fallbackImages, galleryId, onForget });
  latest.current = { fallbackImages, galleryId, onForget };

  useEffect(() => {
    const scheduler = createThumbnailRetryScheduler({
      getGalleryId: () => latest.current.galleryId,
      onForget: (filename) => latest.current.onForget(filename),
    });
    schedulerRef.current = scheduler;
    scheduler.sync(latest.current.fallbackImages);
    return () => {
      scheduler.dispose();
      schedulerRef.current = null;
    };
  }, []);

  useEffect(() => {
    schedulerRef.current?.sync(fallbackImages);
  }, [fallbackImages]);
}
